import { create } from 'zustand';
import { apiService, type TifosiPayload } from '@/services/api';
import { trackUsageEvent } from '@/services/usageEvent';
import { useFavoritesStore } from '@/stores/favoritesStore';
import type { UserProfile, Club } from '@/types';

const PROFILE_KEY = 'tifosi_profile';

function emptyToNull(value: string | undefined | null): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

function buildTifosiPayload(profile: UserProfile): TifosiPayload | null {
  if (!profile.tifosiCle) return null;
  return {
    cle: profile.tifosiCle,
    nom: emptyToNull(profile.nom),
    prenom: emptyToNull(profile.prenom),
    email: emptyToNull(profile.email),
    mobile: emptyToNull(profile.mobile),
    pseudo: emptyToNull(profile.pseudo),
    club: emptyToNull(profile.followedClub?.cle),
  };
}

async function syncTifosi(profile: UserProfile): Promise<void> {
  const payload = buildTifosiPayload(profile);
  if (!payload) return;
  try {
    await apiService.saveTifosi(payload);
  } catch (error) {
    console.error('Tifosi sync error:', error);
  }
}

interface ProfileState extends UserProfile {
  loadProfile: () => Promise<void>;
  /** Récupère la clé Tifosi via l’email si elle manque dans le profil local. */
  ensureTifosiCle: () => Promise<string | undefined>;
  saveProfile: (data: Partial<UserProfile>) => Promise<void>;
  setFollowedClub: (club: Club | null) => Promise<void>;
  signInWithGoogle: (credential: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const defaultProfile: UserProfile = {
  pseudo: '',
  email: '',
  followedClub: null,
  googleId: undefined,
  picture: undefined,
  tifosiCle: undefined,
  nom: undefined,
  prenom: undefined,
  mobile: undefined,
};

interface GoogleIdTokenPayload {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
}

function decodeGoogleCredential(credential: string): GoogleIdTokenPayload | null {
  try {
    const parts = credential.split('.');
    if (parts.length < 2) return null;
    // base64url -> base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as GoogleIdTokenPayload;
  } catch (error) {
    console.error('Google credential decode error:', error);
    return null;
  }
}

function persist(profile: UserProfile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch (e) {
    console.error('Profile save error:', e);
  }
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  ...defaultProfile,

  loadProfile: async () => {
    try {
      const raw = localStorage.getItem(PROFILE_KEY);
      if (raw) {
        const data = JSON.parse(raw) as UserProfile;
        set({
          pseudo: data.pseudo ?? '',
          email: data.email ?? '',
          followedClub: data.followedClub ?? null,
          googleId: data.googleId ?? undefined,
          picture: data.picture ?? undefined,
          tifosiCle: data.tifosiCle ?? undefined,
          nom: data.nom ?? undefined,
          prenom: data.prenom ?? undefined,
          mobile: data.mobile ?? undefined,
        });
      }
    } catch {
      // keep defaults
    }
  },

  ensureTifosiCle: async () => {
    const current = get();
    const existing = (current.tifosiCle || '').trim();
    if (existing) return existing;
    const email = (current.email || '').trim();
    if (!email) return undefined;
    try {
      const tifosi = await apiService.getTifosiByEmail(email);
      const cle = (tifosi?.cle || '').trim();
      if (!cle) return undefined;
      const next: UserProfile = { ...current, tifosiCle: cle };
      set(next);
      persist(next);
      return cle;
    } catch (error) {
      console.error('ensureTifosiCle error:', error);
      return undefined;
    }
  },

  saveProfile: async (data: Partial<UserProfile>) => {
    const current = get();
    const next: UserProfile = {
      pseudo: data.pseudo ?? current.pseudo,
      email: data.email ?? current.email,
      followedClub: data.followedClub ?? current.followedClub,
      googleId: data.googleId ?? current.googleId,
      picture: data.picture ?? current.picture,
      tifosiCle: data.tifosiCle ?? current.tifosiCle,
      nom: data.nom ?? current.nom,
      prenom: data.prenom ?? current.prenom,
      mobile: data.mobile ?? current.mobile,
    };
    set(next);
    persist(next);
    await syncTifosi(next);
  },

  setFollowedClub: async (club: Club | null) => {
    const current = get();
    const next: UserProfile = {
      ...current,
      followedClub: club,
    };
    set(next);
    persist(next);
    await syncTifosi(next);
  },

  signInWithGoogle: async (credential: string) => {
    const payload = decodeGoogleCredential(credential);
    if (!payload || !payload.sub) {
      throw new Error('Identifiant Google invalide.');
    }
    const current = get();
    const email = current.email?.trim() ? current.email : payload.email ?? '';
    const pseudo = current.pseudo?.trim() ? current.pseudo : payload.name ?? payload.given_name ?? '';
    const nom = current.nom?.trim() ? current.nom : payload.family_name;
    const prenom = current.prenom?.trim() ? current.prenom : payload.given_name;

    const intermediate: UserProfile = {
      pseudo,
      email,
      followedClub: current.followedClub,
      googleId: payload.sub,
      picture: payload.picture,
      tifosiCle: current.tifosiCle,
      nom,
      prenom,
      mobile: current.mobile,
    };
    set(intermediate);
    persist(intermediate);

    if (!email) return;

    try {
      const tifosi = await apiService.upsertTifosiFromGoogle({
        email,
        nom,
        prenom,
        pseudo,
        tifosiCle: intermediate.tifosiCle,
      });
      const next: UserProfile = {
        ...intermediate,
        tifosiCle: tifosi.cle || intermediate.tifosiCle,
        nom: intermediate.nom ?? tifosi.nom,
        prenom: intermediate.prenom ?? tifosi.prenom,
        mobile: intermediate.mobile ?? tifosi.mobile,
        pseudo: intermediate.pseudo || tifosi.pseudo || pseudo,
      };
      set(next);
      persist(next);
      if (next.tifosiCle) {
        trackUsageEvent(
          { type: 'login_completed', tifosi: next.tifosiCle },
          { withSource: true, dedupeContext: 'login', dedupeMs: 60_000, skipOnPop: false },
        );
        await useFavoritesStore.getState().syncWithServer(next.tifosiCle);
      }
    } catch (error) {
      console.error('Tifosi upsert error:', error);
    }
  },

  signOut: async () => {
    const current = get();
    const next: UserProfile = {
      pseudo: current.pseudo,
      email: current.email,
      followedClub: current.followedClub,
      googleId: undefined,
      picture: undefined,
      tifosiCle: current.tifosiCle,
      nom: current.nom,
      prenom: current.prenom,
      mobile: current.mobile,
    };
    set(next);
    persist(next);
  },
}));
